import { gsap } from 'gsap';
import { BaseEffectModule } from '../BaseEffectModule.js';

/**
 * Text Animations Module - Handles text splitting and stagger animations
 * Supports words, chars, words+chars, and lines splitting with advanced stagger
 */
export class TextAnimationsModule extends BaseEffectModule {
    constructor() {
        super('textAnimations', {
            version: '1.0.0',
            description: 'Text splitting and stagger animations with various split types',
            dependencies: []
        });
    }

    /**
     * Create state for text animations
     * @param {Element} element - DOM element
     * @param {Object} config - Text animation configuration
     * @returns {Object} Text animation state
     */
    createState(element, config) {
        if (!config.enabled || config.config.splitType === 'none') {
            return {
                enabled: false,
                reason: 'Text animations disabled or no split type specified'
            };
        }
        
        // Split text based on configuration
        const splitResult = this.splitText(element, config.config);
        
        if (!splitResult.elements || splitResult.elements.length === 0) {
            return {
                enabled: false,
                reason: 'No text elements found for splitting'
            };
        }
        
        // Prepare initial and final properties
        const initialProps = this.prepareTextProps(config.initialState);
        const finalProps = this.prepareTextProps(config.finalState);
        
        // Set initial state on text elements
        gsap.set(splitResult.elements, initialProps);
        
        const state = {
            enabled: true,
            config: config,
            elements: splitResult.elements,
            splitInfo: splitResult.info,
            initialProps: initialProps,
            finalProps: finalProps,
            currentProgress: 0,
            targetProgress: 0,
            lastProgress: 0,
            needsUpdate: true,
            isAnimating: false,
            staggerConfig: this.prepareStaggerConfig(config.config, splitResult.elements.length)
        };
        
        // console.log(`📝 Created text animation state: ${splitResult.elements.length} ${config.config.splitType} elements`);
        return state;
    }
    
    /**
     * Update text animations
     * @param {Element} element - DOM element (container)
     * @param {Object} state - Text animation state
     * @param {Object} sharedState - Global shared state
     * @param {number} deltaTime - Time since last frame
     * @param {Object} cache - Element cache
     */
    update(element, state, sharedState, deltaTime, cache) {
        if (!this.isEnabled || !state.enabled || !state.needsUpdate) return;
        
        // Smooth interpolation towards target
        const hasChange = Math.abs(state.currentProgress - state.targetProgress) > 0.001;
        if (hasChange) {
            state.currentProgress += (state.targetProgress - state.currentProgress) * 0.1;
            
            if (Math.abs(state.targetProgress - state.currentProgress) < 0.001) {
                state.currentProgress = state.targetProgress;
                state.needsUpdate = false;
            }
            
            this.applyStaggeredProps(state);
        }
    }
    
    /**
     * Apply interpolated properties with stagger effect
     * @param {Object} state - Text animation state
     */
    applyStaggeredProps(state) {
        if (!state.elements?.length) return;
        
        const progress = state.currentProgress;
        const stagger = state.config.config?.stagger || 0.05;
        
        state.elements.forEach((element, index) => {
            // Simple stagger calculation
            const staggerDelay = (index / (state.elements.length - 1)) * stagger;
            const elementProgress = Math.max(0, Math.min(1, progress - staggerDelay));
            
            // Interpolate properties
            const props = this.interpolateTextProps(
                state.initialProps,
                state.finalProps,
                elementProgress
            );
            
            gsap.set(element, props);
        });
    }
    
    /**
     * Set text animation progress (called by triggers)
     * @param {Object} state - Text animation state
     * @param {number} progress - Target progress (0-1)
     */
    setProgress(state, progress) {
        if (!state?.enabled) return;
        
        const clampedProgress = this.clamp(progress, 0, 1);
        state.targetProgress = clampedProgress;
        state.needsUpdate = true;
        
        // ✅ FIXED: Always apply immediately for smooth progress-based triggers
        state.currentProgress = clampedProgress;
        this.applyStaggeredProps(state);
    }
    
    /**
     * Execute discrete text animation (for non-scrub triggers)
     * @param {Object} state - Text animation state
     * @returns {gsap.Timeline} GSAP timeline
     */
    executeAnimation(state) {
        state.isAnimating = true;
        
        const timeline = gsap.timeline({
            onComplete: () => {
                state.isAnimating = false;
                state.currentProgress = 1;
                state.targetProgress = 1;
            }
        });
        
        const animationProps = {
            ...state.finalProps,
            duration: state.config.timing?.duration || 1,
            delay: state.config.timing?.delay || 0,
            ease: state.config.timing?.ease || 'power2.out',
            stagger: state.staggerConfig
        };
        
        // Add loop settings if enabled
        if (state.config.timing?.loop) {
            animationProps.repeat = -1;
            animationProps.repeatDelay = state.config.timing.loopDelay || 0;
            animationProps.yoyo = state.config.timing.yoyo || false;
        }
        
        timeline.to(state.elements, animationProps);
        
        return timeline;
    }
    
    /**
     * Split text based on configuration
     * @param {Element} element - Container element
     * @param {Object} config - Split configuration
     * @returns {Object} Split result {elements, info}
     */
    splitText(element, config) {
        try {
            const elements = [];
            let splitInfo = {};
            
            switch (config.splitType) {
                case 'words':
                    const words = this.splitWords(element, config.wordClass);
                    elements.push(...words);
                    splitInfo = { type: 'words', count: words.length };
                    break;
                    
                case 'chars':
                    const chars = this.splitChars(element, config.charClass);
                    elements.push(...chars);
                    splitInfo = { type: 'chars', count: chars.length };
                    break;
                    
                case 'words,chars':
                    const wordsAndChars = this.splitWordsAndChars(element, config.wordClass, config.charClass);
                    elements.push(...wordsAndChars.chars); // Animate characters
                    splitInfo = { 
                        type: 'words,chars', 
                        wordCount: wordsAndChars.words.length,
                        charCount: wordsAndChars.chars.length 
                    };
                    break;
                    
                case 'lines':
                    const lines = this.splitLines(element, config.lineClass);
                    elements.push(...lines);
                    splitInfo = { type: 'lines', count: lines.length };
                    break;
                    
                default:
                    console.warn(`Unknown split type: ${config.splitType}`);
                    return { elements: [], info: {} };
            }
            
            // Optimize elements for animation
            this.optimizeElementsForAnimation(elements);
            
            return { elements, info: splitInfo };
            
        } catch (error) {
            console.error('Error splitting text:', error);
            return { elements: [], info: {} };
        }
    }
    
    /**
     * Split text into words
     * @param {Element} element - Container element
     * @param {string} wordClass - CSS class for word elements
     * @returns {Array} Word elements
     */
    splitWords(element, wordClass = 'off-word') {
        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );
        
        const textNodes = [];
        let node;
        
        while (node = walker.nextNode()) {
            if (node.textContent.trim() !== '') {
                textNodes.push(node);
            }
        }
        
        const wordElements = [];
        
        textNodes.forEach(textNode => {
            const words = textNode.textContent.split(/(\s+)/);
            const fragment = document.createDocumentFragment();
            
            words.forEach(word => {
                if (word.trim() !== '') {
                    const span = document.createElement('span');
                    span.className = wordClass;
                    span.textContent = word;
                    span.style.display = 'inline-block';
                    fragment.appendChild(span);
                    wordElements.push(span);
                } else if (word !== '') {
                    // Preserve spaces
                    fragment.appendChild(document.createTextNode(word));
                }
            });
            
            textNode.parentNode.replaceChild(fragment, textNode);
        });
        
        return wordElements;
    }
    
    /**
     * Split text into characters
     * @param {Element} element - Container element
     * @param {string} charClass - CSS class for character elements
     * @returns {Array} Character elements
     */
    splitChars(element, charClass = 'off-char') {
        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );
        
        const textNodes = [];
        let node;
        
        while (node = walker.nextNode()) {
            if (node.textContent.trim() !== '') {
                textNodes.push(node);
            }
        }
        
        const charElements = [];
        
        textNodes.forEach(textNode => {
            const chars = Array.from(textNode.textContent);
            const fragment = document.createDocumentFragment();
            
            chars.forEach(char => {
                if (char === ' ') {
                    fragment.appendChild(document.createTextNode(char));
                } else {
                    const span = document.createElement('span');
                    span.className = charClass;
                    span.textContent = char;
                    span.style.display = 'inline-block';
                    span.style.whiteSpace = 'nowrap';
                    fragment.appendChild(span);
                    charElements.push(span);
                }
            });
            
            textNode.parentNode.replaceChild(fragment, textNode);
        });
        
        return charElements;
    }
    
    /**
     * Split into words first, then chars within words
     * @param {Element} element - Container element
     * @param {string} wordClass - CSS class for word elements
     * @param {string} charClass - CSS class for character elements
     * @returns {Object} {words, chars}
     */
    splitWordsAndChars(element, wordClass = 'off-word', charClass = 'off-char') {
        // First split into words
        const wordElements = this.splitWords(element, wordClass);
        
        // Then split each word into characters
        const allCharElements = [];
        wordElements.forEach(wordElement => {
            const charElements = this.splitChars(wordElement, charClass);
            allCharElements.push(...charElements);
            
            // Set word wrapper styles
            wordElement.style.whiteSpace = 'nowrap';
            wordElement.style.display = 'inline-block';
        });
        
        return {
            words: wordElements,
            chars: allCharElements
        };
    }
    
    /**
     * Split into lines
     * @param {Element} element - Container element
     * @param {string} lineClass - CSS class for line elements
     * @returns {Array} Line elements
     */
    splitLines(element, lineClass = 'off-line') {
        const text = element.innerHTML;
        const lines = text.split('<br>');
        
        const lineElements = [];
        
        if (lines.length > 1) {
            element.innerHTML = '';
            lines.forEach((line, index) => {
                const div = document.createElement('div');
                div.className = lineClass;
                div.innerHTML = line;
                element.appendChild(div);
                lineElements.push(div);
            });
        } else {
            // Fallback: treat whole element as one line
            const wrapper = document.createElement('div');
            wrapper.className = lineClass;
            wrapper.innerHTML = element.innerHTML;
            element.innerHTML = '';
            element.appendChild(wrapper);
            lineElements.push(wrapper);
        }
        
        return lineElements;
    }
    
    /**
     * Prepare text properties for GSAP
     * @param {Object} rawProps - Raw properties from configuration
     * @returns {Object} GSAP-ready properties
     */
    prepareTextProps(rawProps) {
        const props = {};
        
        // Basic properties
        ['opacity', 'x', 'y', 'rotation', 'scale', 'skewX', 'skewY'].forEach(prop => {
            if (rawProps[prop] !== undefined) {
                props[prop] = rawProps[prop];
            }
        });
        
        // Handle blur filter
        if (rawProps.blur !== undefined) {
            if (rawProps.blur > 0) {
                props.filter = `blur(${rawProps.blur}px)`;
            } else {
                props.filter = 'none';
            }
        }
        
        // Handle color
        if (rawProps.color && rawProps.color !== '') {
            props.color = rawProps.color;
        }
        
        return props;
    }
    
    /**
     * Interpolate text properties
     * @param {Object} initialProps - Initial properties
     * @param {Object} finalProps - Final properties
     * @param {number} progress - Progress (0-1)
     * @returns {Object} Interpolated properties
     */
    interpolateTextProps(initialProps, finalProps, progress) {
        const interpolated = {};
        
        // Get all unique properties
        const allProps = new Set([
            ...Object.keys(initialProps),
            ...Object.keys(finalProps)
        ]);
        
        allProps.forEach(prop => {
            const initialValue = initialProps[prop] !== undefined ? initialProps[prop] : this.getDefaultTextValue(prop);
            const finalValue = finalProps[prop] !== undefined ? finalProps[prop] : this.getDefaultTextValue(prop);
            
            if (prop === 'color') {
                interpolated[prop] = progress > 0.5 ? finalValue : initialValue;
            } else if (prop === 'filter') {
                interpolated[prop] = this.interpolateFilter(initialValue, finalValue, progress);
            } else if (typeof initialValue === 'number' && typeof finalValue === 'number') {
                interpolated[prop] = this.lerp(initialValue, finalValue, progress);
            } else {
                interpolated[prop] = progress > 0.5 ? finalValue : initialValue;
            }
        });
        
        return interpolated;
    }
    
    /**
     * Get default value for text property
     * @param {string} prop - Property name
     * @returns {*} Default value
     */
    getDefaultTextValue(prop) {
        const defaults = {
            opacity: 1,
            x: 0,
            y: 0,
            rotation: 0,
            scale: 1,
            skewX: 0,
            skewY: 0,
            blur: 0,
            color: 'inherit'
        };
        
        return defaults[prop] || 0;
    }
    
    /**
     * Interpolate filter values for text
     * @param {string} initial - Initial filter
     * @param {string} final - Final filter
     * @param {number} progress - Progress (0-1)
     * @returns {string} Interpolated filter
     */
    interpolateFilter(initial, final, progress) {
        const initialBlur = this.extractBlurValue(initial);
        const finalBlur = this.extractBlurValue(final);
        
        if (initialBlur !== null && finalBlur !== null) {
            const interpolatedBlur = this.lerp(initialBlur, finalBlur, progress);
            
            // ❌ OLD CODE: Caused jumps to 'none'
            // return interpolatedBlur > 0 ? `blur(${interpolatedBlur}px)` : 'none';
            
            // ✅ BLUR FIX: Always return blur(Xpx) format for smooth animation
            return `blur(${interpolatedBlur}px)`; // Even blur(0px) animates smoothly!
        }
        
        return progress > 0.5 ? final : initial;
    }
    
    /**
     * Extract blur value from filter string
     * @param {string} filterString - Filter CSS string
     * @returns {number|null} Blur value in pixels
     */
    extractBlurValue(filterString) {
        if (!filterString || filterString === 'none') return 0;
        
        const match = filterString.match(/blur\((\d+(?:\.\d+)?)px\)/);
        return match ? parseFloat(match[1]) : null;
    }
    
    /**
     * Prepare stagger configuration
     * @param {Object} config - Stagger configuration
     * @param {number} elementCount - Number of elements
     * @returns {Object} GSAP stagger configuration
     */
    prepareStaggerConfig(config, elementCount) {
        return {
            amount: (config?.stagger || 0.05) * (elementCount - 1),
            from: config?.staggerFrom || 'start',
            ease: 'none'
        };
    }
    
    /**
     * Optimize elements for animation performance
     * @param {Array} elements - Text elements to optimize
     */
    optimizeElementsForAnimation(elements) {
        elements.forEach(element => {
            element.style.willChange = 'transform, opacity';
            element.style.transform = 'translateZ(0)';
            element.style.backfaceVisibility = 'hidden';
            element.style.webkitFontSmoothing = 'antialiased';
            element.style.mozOsxFontSmoothing = 'grayscale';
        });
    }
    
    /**
     * Reset text animation to initial state
     * @param {Object} state - Text animation state
     */
    reset(state) {
        state.currentProgress = 0;
        state.targetProgress = 0;
        state.needsUpdate = true;
        state.isAnimating = false;
        
        if (state.elements && state.elements.length > 0) {
            gsap.set(state.elements, state.initialProps);
        }
    }
    
    /**
     * Cleanup text animation state
     * @param {Object} state - Text animation state to cleanup
     */
    cleanup(state) {
        // Kill any ongoing animations
        if (state.isAnimating && state.elements) {
            gsap.killTweensOf(state.elements);
        }
        
        // Clean up elements performance optimizations
        if (state.elements) {
            state.elements.forEach(element => {
                element.style.willChange = 'auto';
                element.style.transform = '';
                element.style.backfaceVisibility = '';
            });
        }
        
        // Clear references
        state.elements = null;
        state.initialProps = null;
        state.finalProps = null;
        state.config = null;
        
        // console.log(`🗑️ Cleaned up text animation state`);
    }
}

export default TextAnimationsModule;